import { useQuery } from '@tanstack/react-query'
import api from '../../../api'
import { cn } from '../../../lib/utils'
import { DataTable } from '../../charts/DataTable'
import { formatCurrency } from '../../../config/chartTheme'
import { DetailSkeleton } from '../../finance/SkeletonStates'
import { AlertTriangle } from 'lucide-react'

interface Level4JournalEntryProps {
  jeId: number
  onDrillException: (exceptionId: number, label: string) => void
}

export function Level4JournalEntry({ jeId, onDrillException }: Level4JournalEntryProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['je-detail', jeId],
    queryFn: () => api.get(`/posted-jes/${jeId}`).then(r => r.data),
  })

  if (isLoading) return <DetailSkeleton />

  const je = data || {}
  const lines = je.lines || je.journal_entry_lines || []

  const totalDebit = lines.reduce((s: number, l: any) => s + (l.debit_amount || 0), 0)
  const totalCredit = lines.reduce((s: number, l: any) => s + (l.credit_amount || 0), 0)

  return (
    <div className="space-y-4">
      {/* JE Header */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
      )}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Document', value: je.document_number || `#${jeId}` },
            { label: 'Date', value: je.posting_date || '\u2014' },
            { label: 'Entity', value: je.entity_code || '\u2014' },
            { label: 'Status', value: je.status || je.je_status || '\u2014' },
          ].map((item, i) => (
            <div key={i}>
              <div className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)] mb-1">
                {item.label}
              </div>
              <div className="font-['JetBrains_Mono',monospace] text-[13px] text-[var(--text-primary,#F0F2F5)]">
                {item.value}
              </div>
            </div>
          ))}
        </div>
        {je.description && (
          <div className="mt-3 text-[12px] text-[var(--text-secondary,#94A3B8)]">
            {je.description}
          </div>
        )}
      </div>

      {/* Lines Table */}
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-4',
      )}>
        <div className="flex items-center gap-1.5 mb-3">
          <div className="h-1.5 w-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
          <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
            Journal Entry Lines
          </span>
        </div>
        {lines.length > 0 ? (
          <>
            <DataTable
              columns={[
                { field: 'line_number', headerName: '#', width: 50 },
                { field: 'account_code', headerName: 'Account', width: 110 },
                { field: 'account_name', headerName: 'Name', flex: 2 },
                { field: 'description', headerName: 'Description', flex: 2 },
                { field: 'debit_amount', headerName: 'Debit', type: 'currency', width: 120 },
                { field: 'credit_amount', headerName: 'Credit', type: 'currency', width: 120 },
              ]}
              data={lines}
              height={56 + lines.length * 42}
            />
            {/* Totals */}
            <div className="flex justify-end gap-8 mt-3 px-3">
              <div>
                <span className="text-[10px] uppercase text-[var(--text-muted,#64748B)] mr-2">Total Debit</span>
                <span className="font-['JetBrains_Mono',monospace] text-[13px] font-semibold text-[var(--text-primary,#F0F2F5)]">
                  {formatCurrency(totalDebit, false)}
                </span>
              </div>
              <div>
                <span className="text-[10px] uppercase text-[var(--text-muted,#64748B)] mr-2">Total Credit</span>
                <span className="font-['JetBrains_Mono',monospace] text-[13px] font-semibold text-[var(--text-primary,#F0F2F5)]">
                  {formatCurrency(totalCredit, false)}
                </span>
              </div>
              {Math.abs(totalDebit - totalCredit) > 0.01 && (
                <div className="flex items-center gap-1 text-red-400 text-[11px]">
                  <AlertTriangle className="h-3 w-3" />
                  Out of balance
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-[13px] text-[var(--text-muted,#64748B)]">
            No line details available
          </div>
        )}
      </div>
    </div>
  )
}
